// Vireo — brand atoms. Ported from the Claude Design handoff (vireo-base.jsx).
// Pure presentational SVG/markup, safe in server or client components.

import React from 'react';

// ─── LOGO — continuous-line bird-in-flight mark + italic wordmark ───

export function VireoMark({
  size = 28,
  color = 'var(--v-ink)',
  dot = 'var(--v-amber)',
  style,
}: {
  size?: number;
  color?: string;
  dot?: string;
  style?: React.CSSProperties;
}) {
  // Canonical mark — v2 kit geometry (Design/vireo-brand/current/logos/svg/
  // Bird-Mark/Vireo-Mark-Full.svg), viewBox tightened to the stroke+eye bounds
  // so the bird fills its slot. One continuous line (bird in flight / open eye /
  // soft "v") with the amber dot as the eye and the data point.
  return (
    <svg
      width={size * (352 / 104)}
      height={size}
      viewBox="-176 -43 352 104"
      style={{ display: 'block', ...style }}
      aria-hidden
    >
      <path
        d="M -170 55 C -98.6 -53, -49.3 -53, 0 55 C 49.3 -53, 98.6 -53, 170 55"
        fill="none"
        stroke={color}
        strokeWidth="12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="0" cy="-25" r="18" fill={dot} />
    </svg>
  );
}

export function VireoWordmark({
  size = 24,
  color = 'var(--v-ink)',
  dot = 'var(--v-amber)',
  showWord = true,
  style,
}: {
  size?: number;
  color?: string;
  dot?: string;
  showWord?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, ...style }}>
      <VireoMark size={size} color={color} dot={dot} />
      {showWord && (
        <span
          className="v-serif"
          style={{ fontSize: size * 1.35, fontWeight: 500, color, letterSpacing: -0.02, lineHeight: 1 }}
        >
          Vireo
        </span>
      )}
    </div>
  );
}

// ─── ICONS — sized to match Inter weight, 1.7 stroke ───

const ICON_PATHS: Record<string, React.ReactNode> = {
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.4-4.4" /></>,
  mic: <><rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5 11a7 7 0 0014 0M12 18v3" /></>,
  heart: <path d="M12 21s-7-4.5-9.5-9.5C.8 8 2.8 4 6.5 4c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3 3.7 0 5.7 4 4 7.5C19 16.5 12 21 12 21z" />,
  // ⚠️ `fill="currentColor"` ON THE PATH, not on the <svg>. The svg element
  // below hardcodes `fill="none"` for the whole set, which is right for 40-odd
  // stroked glyphs and wrong for this one: `heart-f` is the FILLED heart and it
  // has been rendering as an outline identical to `heart` since it was added,
  // on every surface that uses it (the feed rail's Save, the 88px double-tap
  // burst, PhotoTour's two save buttons, FeatureOverlay's pin, and the Folio
  // tab in the app rail). `play` above carries its own fill for the same
  // reason; this is that pattern, not a new one.
  'heart-f': <path d="M12 21s-7-4.5-9.5-9.5C.8 8 2.8 4 6.5 4c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3 3.7 0 5.7 4 4 7.5C19 16.5 12 21 12 21z" fill="currentColor" />,
  share: <><path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7" /><path d="M16 6l-4-4-4 4M12 2v14" /></>,
  bell: <><path d="M6 8a6 6 0 1112 0c0 7 3 7 3 9H3c0-2 3-2 3-9zM10 21h4" /></>,
  // Added for the landing's newsletter prompt. An open envelope flap rather
  // than a closed rectangle: the closed one is indistinguishable from `sq` at
  // 16px. Drawn to the same 24px box and 1.6 stroke as the rest of the set.
  mail: <><rect x="3" y="5.5" width="18" height="13" rx="2.2" /><path d="M3.6 6.6L12 13l8.4-6.4" /></>,
  filter: <path d="M3 5h18M6 12h12M10 19h4" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  compass: <><circle cx="12" cy="12" r="9" /><path d="M15 9l-2 6-4 1 2-6 4-1z" /></>,
  x: <path d="M5 5l14 14M19 5L5 19" />,
  check: <path d="M4 12.5l5 5L20 6" />,
  'chev-r': <path d="M9 6l6 6-6 6" />,
  'chev-l': <path d="M15 6l-6 6 6 6" />,
  'chev-d': <path d="M6 9l6 6 6-6" />,
  'chev-u': <path d="M6 15l6-6 6 6" />,
  'arrow-r': <path d="M5 12h14M13 5l7 7-7 7" />,
  // The mirror of `arrow-r`, for a CTA that sits to the LEFT of what it means.
  'arrow-l': <path d="M19 12H5M11 5l-7 7 7 7" />,
  'arrow-up-r': <path d="M7 17L17 7M9 7h8v8" />,
  play: <path d="M7 4l13 8-13 8V4z" fill="currentColor" />,
  sparkle: <><path d="M12 3l1.6 5L19 10l-5.4 1.4L12 17l-1.6-5.6L5 10l5.4-1z" /><path d="M19 17l.6 1.6L21.2 19.2l-1.6.4L19 22l-.6-2.4L16.8 19.2z" /></>,
  bed: <path d="M3 18v-7h18v7M3 14h18M7 11V8a2 2 0 012-2h6a2 2 0 012 2v3M3 18v3M21 18v3" />,
  bath: <path d="M4 12h16v4a4 4 0 01-4 4H8a4 4 0 01-4-4v-4zM6 12V6a2 2 0 014 0M3 12h18" />,
  sq: <path d="M3 21v-4l12-12 4 4-12 12H3z" />,
  pin: <><path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></>,
  // Added for the landing's side rail, which needs a "Home" that means this
  // page rather than a house for sale. Drawn to the same 24px box and 1.6
  // stroke as the rest of the set, so it sits level with `play` and `pin`.
  home: <><path d="M4 11l8-6.5 8 6.5" /><path d="M6.5 9.8V20h11V9.8" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  // Added for the feed's agent contact card. `mic` was standing in for this and
  // read as "voice" next to a phone number. Drawn to the same 24px box and 1.6
  // stroke as the rest of the set.
  phone: <path d="M6.5 3.5h3l1.5 4-2 1.5a12 12 0 006 6l1.5-2 4 1.5v3a2 2 0 01-2.2 2A17 17 0 014.5 5.7 2 2 0 016.5 3.5z" />,
  // Added for the feed rail's "Book". An OPEN book with a spine, not a closed
  // cover: the closed one reads as a notebook at 17px, and the thing it opens
  // is something you flip through.
  book: <><path d="M12 6.5v13" /><path d="M12 6.5C10.5 5.2 8.6 4.5 6 4.5H3.5v13H6c2.6 0 4.5.7 6 2" /><path d="M12 6.5c1.5-1.3 3.4-2 6-2h2.5v13H18c-2.6 0-4.5.7-6 2" /></>,
  sun: <><circle cx="12" cy="12" r="4.5" /><path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.6 4.6l1.8 1.8M17.6 17.6l1.8 1.8M19.4 4.6l-1.8 1.8M6.4 17.6l-1.8 1.8" /></>,
  moon: <path d="M20 14.5A8.5 8.5 0 119.5 4a6.7 6.7 0 0010.5 10.5z" />,
  volume: <><path d="M4 9v6h4l5 4V5L8 9H4z" /><path d="M16.5 8.5a5 5 0 010 7M19 6a8.5 8.5 0 010 12" /></>,
  // Added for the marketing pages' feature cards and the book's "Ask about
  // this" chip. `speaker` is the SPEAKING one -- a cone with two waves -- as
  // distinct from `mic` (listening) and `volume` (playback level). All three
  // exist because the marketing copy uses all three ideas in one page.
  speaker: <><path d="M4 9.5v5h3.5l4.5 3.5v-12L7.5 9.5H4z" /><path d="M16 9.8a4 4 0 010 4.4M18.8 7.4a7.5 7.5 0 010 9.2" /></>,
  // Stacked planes, for "more than one of the same thing made at once" --
  // the image studio's carousels, and the brokerage white-label card.
  layers: <><path d="M12 3.2L21 8l-9 4.8L3 8l9-4.8z" /><path d="M3 12.4l9 4.8 9-4.8" /><path d="M3 16.6l9 4.8 9-4.8" /></>,
  lock: <><rect x="4" y="10.5" width="16" height="10" rx="2.2" /><path d="M8 10.5V7.6a4 4 0 018 0v2.9" /></>,
  'volume-x': <><path d="M4 9v6h4l5 4V5L8 9H4z" /><path d="M17 9.5l4 5M21 9.5l-4 5" /></>,
};

export function VIcon({
  name,
  size = 16,
  stroke = 1.7,
  style,
}: {
  name: string;
  size?: number;
  stroke?: number;
  style?: React.CSSProperties;
}) {
  const path = ICON_PATHS[name];
  if (!path) return null;
  const { color, ...rest } = style ?? {};
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color, ...rest }}
      aria-hidden
    >
      {path}
    </svg>
  );
}

// ─── Phonetic / pronunciation chip — used once on the landing ───

export function VireoPronounceChip({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className="v-mono"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: compact ? '4px 10px' : '6px 12px',
        borderRadius: 999,
        background: 'var(--v-amber-soft)',
        border: '1px solid var(--v-line-2)',
        fontSize: 11,
        letterSpacing: 0.04,
        color: 'var(--v-muted)',
      }}
    >
      <span style={{ color: 'var(--v-ink)', fontWeight: 600 }}>VEER-ee-oh</span>
      <span style={{ width: 1, height: 10, background: 'var(--v-line)' }} />
      <span style={{ textTransform: 'none', letterSpacing: 0 }}>
        n. a small songbird with extraordinary eyesight
      </span>
    </span>
  );
}

// ─── Waveform — small, used in voice moments ───

export function VWave({
  bars = 22,
  color = 'currentColor',
  height = 22,
  active = true,
}: {
  bars?: number;
  color?: string;
  height?: number;
  active?: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, height }}>
      {Array.from({ length: bars }).map((_, i) => {
        const h = 25 + ((i * 41) % 75);
        return (
          <span
            key={i}
            style={{
              width: 2,
              height: '100%',
              borderRadius: 2,
              background: color,
              transform: `scaleY(${h / 100})`,
              transformOrigin: 'center',
              animation: active
                ? `thresh-wave ${0.7 + (i % 5) * 0.12}s ease-in-out ${(i % 7) * 0.06}s infinite`
                : 'none',
            }}
          />
        );
      })}
    </div>
  );
}
